import {useContext, useState} from 'react';
import {FilterBar, FilterTabs, FilterChip} from '@/components/pages/messages/MessagesFilter.styles';
import {useQuery} from '@tanstack/react-query';
import {useShallow} from 'zustand/react/shallow';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {useMessageStore} from '@/store/message_store';
import {useMessageThreadInbox} from '@/hooks/useMessageThreadInbox';
import WidgetPopover from '@common/widgets/popover/WidgetPopover';
import WidgetOnlyShowUnread from '@common/widgets/WidgetOnlyShowUnread';
import MessagesWidgetIcon from '@/components/pages/messages/MessagesWidgetIcon';
import MessageThreadItem from '@/components/pages/messages/MessageThreadItem';

enum FilterTab {
  ALL = 'All',
  CLIENT = 'Client',
  TASK = 'Task',
  REFERRAL = 'Referral',
}

const TABS: FilterTab[] = [FilterTab.ALL, FilterTab.CLIENT, FilterTab.TASK, FilterTab.REFERRAL];

export default function MessagesWidget() {
  const { carecoApi } = useContext(CarecoApiContext);
  const currentUser = useAuthStore((state) => state.currentUser);
  const [activeFilter, setActiveFilter] = useState<FilterTab>(FilterTab.ALL);

  const [popoverOpen, setPopoverOpen] = useMessageStore(useShallow((state) => [
    state.popoverOpen,
    state.setPopoverOpen,
  ]));

  const { data: currentFetchedUser } = useQuery({
    queryKey: ['currentUser', currentUser?.id],
    queryFn: () => carecoApi?.getUser(currentUser?.id ?? ''),
    enabled: popoverOpen && !!currentUser && !!carecoApi,
  });

  const {
    isApiLoading,
    isFetched,
    error,
    messageThreadSummaries,
    totalMessages,
    totalUnreadMessages,
    markAllAsRead
  } = useMessageThreadInbox({
    onlyShowUnreadMessages: currentFetchedUser?.only_show_unread_messages ?? false,
    enabled: popoverOpen && !!currentFetchedUser,
    threadType: activeFilter === FilterTab.ALL ? undefined : activeFilter,
  });

  return (
    <WidgetPopover
      isOpen={popoverOpen}
      handleClose={setPopoverOpen}
      elements={messageThreadSummaries}
      totalElements={totalMessages}
      totalUnreadElements={totalUnreadMessages}
      dateProperty="latestMessage.createdAt"
      title="Messages"
      route="/messages"
      count={5}
      isLoading={!isFetched}
      isApiLoading={isApiLoading}
      error={error}
      markAllAsRead={markAllAsRead}
      render={(messageThreadSummary) => (
        <MessageThreadItem key={messageThreadSummary.id} messageThreadSummary={messageThreadSummary} concise />
      )}
      header={currentFetchedUser && (
        <WidgetOnlyShowUnread
          queryKey={(currentUser) => ['messageThreadInbox', currentUser.only_show_unread_messages]}
          currentFetchedUser={currentFetchedUser}
          property="only_show_unread_messages"
          label="Messages"
        />
      )}
      filterBar={
        <FilterBar>
          <FilterTabs>
            {TABS.map(tab => (
              <FilterChip key={tab} active={activeFilter === tab} onClick={() => setActiveFilter(tab)}>
                {tab}
              </FilterChip>
            ))}
          </FilterTabs>
        </FilterBar>
      }
    >
      <MessagesWidgetIcon />
    </WidgetPopover>
  );
}
